import { FolderKanban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/format';
import { useProjects } from '../queries';

/**
 * Pupil dashboard card: the teams they are on, soonest due first. The API
 * orders by creation, so the sort happens here — the page size is tiny.
 */
export function MyProjectsCard() {
  const { t, i18n } = useTranslation(['projects']);
  const query = useProjects({ page_size: 5 });

  const rows = [...(query.data?.results ?? [])].sort((a, b) => {
    if (!a.due_at) return 1;
    if (!b.due_at) return -1;
    return a.due_at.localeCompare(b.due_at);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('projects:cards.myProjectsTitle')}</CardTitle>
        <CardDescription>{t('projects:cards.myProjectsDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {query.isLoading ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('projects:cards.myProjectsEmpty')}</p>
        ) : (
          <>
            <ul className="space-y-1">
              {rows.map(project => (
                <li key={project.public_id}>
                  <Link
                    to={`/projects/${project.public_id}`}
                    className="hover:bg-accent flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <FolderKanban className="text-muted-foreground size-4 shrink-0" />
                    <span className="font-medium">{project.title}</span>
                    <span className="text-muted-foreground">
                      {project.members.length > 0
                        ? t('projects:list.team', { count: project.members.length })
                        : t('projects:list.noTeam')}
                    </span>
                    <span className="flex-1" />
                    <span className="text-muted-foreground">
                      {project.due_at
                        ? t('projects:list.due', {
                            date: formatDate(project.due_at, i18n.language),
                          })
                        : t('projects:list.noDue')}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <Button variant="link" size="sm" asChild className="px-2">
              <Link to="/projects">{t('projects:classSection.viewAll')}</Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
